import { EyeIcon } from "@heroicons/react/24/solid";
import { ActionIcon, Card, Checkbox, Divider, Pagination, Text, TextInput, ThemeIcon, useMantineTheme } from "@mantine/core";
import { DeleteOutline, Download, RefreshOutlined } from "@mui/icons-material";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import Icon from "src/components/common/Icons/Icons/Icon";
import { ProcessedComplaint } from "src/interfaces/Complaints";

const TableRow = ({
  item,
  isEven,
  handlePress,
}: {
  item: ProcessedComplaint;
  isEven: boolean;
  handlePress: (item: ProcessedComplaint) => void
}) => {
  const formattedDate = new Date(item?.created_at).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });

  return (
    <div 
      onClick={() => handlePress(item)}
      className={`grid grid-cols-12 min-h-[80px] items-center px-4 
      ${isEven ? 'bg-gray-100' : 'bg-white'}
      hover:bg-gray-200 transition-colors duration-150 cursor-pointer`}>
      <div className="col-span-1">
        <Text size="sm">{formattedDate}</Text>
      </div>
      <div className="col-span-3 pr-4">
        <Text size="sm" align='center' fw={600} className="line-clamp-2">
          {item?.feedback || ''}
        </Text>
      </div>
      <div className="col-span-3 pr-4">
        <Text size="sm" className="line-clamp-3">
          {item?.flagged_message}
        </Text>
      </div>
      <div className="col-span-2 pr-4">
        <Text size="sm">{item?.child_username}</Text>
      </div>
      <div className="col-span-2 pr-4">
        <Text size="sm">{item?.parent_email}</Text>
      </div>
      <div className="col-span-1">
        <Text size="sm" align='center' className="capitalize">{item?.status}</Text>
      </div>
    </div>
  );
};


interface ScrollableTableProps {
  data: ProcessedComplaint[]
  handleItemPress: (item: ProcessedComplaint) => void
 }


export const ComplaintsTable = (props: ScrollableTableProps) => {
  const {data, handleItemPress} = props
  const checkbox = useRef<HTMLInputElement | null>(null);
  const [checked, setChecked] = useState(false);
  const [indeterminate, setIndeterminate] = useState(false);
  const [selectedData, setSelectedData] = useState<ProcessedComplaint[]>([])
  const theme = useMantineTheme()

  const [query, setQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const [statusFilter, setStatusFilter] = useState<'all' | 'pending' | 'resolved'>('all');

  const filteredData = data.filter(item => {
    const matchesQuery = query
      ? item.parent_email.toLowerCase().includes(query.toLowerCase())
      : true;
    
    const matchesStatus = statusFilter === 'all' 
      ? true 
      : item?.status?.toLowerCase() === statusFilter;

    return matchesQuery && matchesStatus;
  });

  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const currentData = filteredData.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  useLayoutEffect(() => {
    const isIndeterminate =
      selectedData.length > 0 && selectedData.length < data.length;
    setChecked(selectedData.length === data.length);
    setIndeterminate(isIndeterminate);
    if (checkbox.current) checkbox.current.indeterminate = isIndeterminate;
  }, [selectedData, data.length]);


  return (
    <>
      <Card p={0} withBorder className="h-[500px] overflow-y-auto flex flex-col my-4 rounded-lg">
        <div className={`sticky top-0 flex-col flex py-4 z-40 ${theme.colorScheme === "dark" ? "bg-black" : "bg-white"} px-4 border-b shadow-sm`}>
          <div className="flex gap-4 mb-4">
            <select 
              className="px-3 py-1 border rounded-md text-sm"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as 'all' | 'pending' | 'resolved')}
            >
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="resolved">Resolved</option>
            </select>
          </div>

          <div className="grid grid-cols-12 items-center">
            <div className="col-span-1">
              <Text size="sm" fw={600}>Date</Text>
            </div>
            <div className="col-span-3 pr-4">
              <Text size="sm" align="center" fw={600}>Complaint</Text>
            </div>
            <div className="col-span-3 pr-4">
              <Text size="sm" fw={600}>Flagged Message</Text>
            </div>
            <div className="col-span-2 pr-4">
              <Text size="sm" fw={600}>Student Username</Text>
            </div>
            <div className="col-span-2 pr-4">
              <Text size="sm" fw={600}>Parent Email</Text>
            </div>
            <div className="col-span-1">
              <Text size="sm" align='center' fw={600}>Status</Text>
            </div>
          </div>
        </div>

        {currentData.length > 0 ? (
          currentData.map((item, index) => (
            <div key={item?.id}>
              <TableRow
                handlePress={handleItemPress}
                item={item}
                isEven={index % 2 === 0}
              />
            </div>
          ))
        ) : (
          <div className="flex flex-1 justify-center items-center py-12">
            <Text fw={600} size="sm" color="dimmed">No complaints found</Text>
          </div>
        )}
      </Card>
      <div className=" w-full flex justify-center items-center"><Pagination total={totalPages} onChange={setCurrentPage} value={currentPage} color="dark" size="sm" radius="md" withControls={false} withEdges /></div>
    </>
  );
};
